import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button, Card, Image, Text, Group, ActionIcon, SimpleGrid, Center, Stack, Loader } from "@mantine/core";
import { IconWorld, IconHeart, IconGitFork } from "@tabler/icons-react";
import { useUserStore } from "../stores/userStore";
import { LikersModal } from "./likers-modal";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function CommunityPage() {
  const [variants, setVariants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const token = useUserStore((state) => state.token);

  const [likers, setLikers] = useState([]);
  const [isLikersModalOpen, setIsLikersModalOpen] = useState(false);
  const [selectedVariantName, setSelectedVariantName] = useState("");
  const [likingInProgress, setLikingInProgress] = useState([]);

  const fetchPublicVariants = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/variants/public`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch public variants');
      const data = await response.json();
      setVariants(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPublicVariants();
  }, [fetchPublicVariants]);

  const handleLoadVariant = (variant) => {
    navigate(`/generator?variantId=${variant.id}`);
  };

  const handleLike = async (variantId) => {
    if (likingInProgress.includes(variantId)) return;

    setLikingInProgress(prev => [...prev, variantId]);
    try {
      const response = await fetch(`${API_URL}/api/variants/${variantId}/like`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to update like status');
      }
      const updatedVariant = await response.json();
      setVariants(variants.map(v => v.id === variantId ? { ...v, likes: updatedVariant.likes } : v));
    } catch (err) {
      setError(err.message);
    } finally {
      setLikingInProgress(prev => prev.filter(id => id !== variantId));
    }
  };

  const handleShowLikers = async (variant) => {
    if (!variant.likes || variant.likes.length === 0) return;
    try {
      const response = await fetch(`${API_URL}/api/variants/${variant.id}/likers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch likers');
      }
      const data = await response.json();
      setLikers(data);
      setSelectedVariantName(variant.name);
      setIsLikersModalOpen(true);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Stack h="100%">
      <Stack gap={5}>
        <Group>
            <IconWorld size={24} />
            <Text size="xl" fw={600}>Community Variants</Text>
        </Group>
        <Text c="dimmed">Explore and load variants created by the community.</Text>
      </Stack>

      {error && <Text c="red" size="sm">{error}</Text>}

      {isLoading ? (
        <Center h={200}>
            <Loader color="cyan" />
        </Center>
      ) : variants.length === 0 ? (
        <Center h={300} style={{ border: '1px dashed var(--mantine-color-dimmed)', borderRadius: 'var(--mantine-radius-md)' }}>
          <Stack align="center" gap="xs">
            <Text size="xl" fw={700}>No public variants yet</Text>
            <Text c="dimmed" size="sm">Be the first to share a variant with the community!</Text>
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
          {variants.map((variant) => (
            <Card key={variant.id} padding="lg" radius="md" withBorder>
              <Card.Section>
                {variant.image ? (
                  <Image src={variant.image} height={220} alt={variant.name} fit="cover" />
                ) : (
                  <div style={{ height: 220, backgroundColor: 'var(--mantine-color-gray-1)' }} />
                )}
              </Card.Section>

              <Stack mt="md" mb="xs" gap={5} style={{ flex: 1 }}>
                  <Text fw={500} truncate>{variant.name}</Text>
                  <Text size="xs" c="dimmed">
                      By {variant.user?.name || 'Anonymous'} on {new Date(variant.createdAt).toLocaleDateString()}
                  </Text>
                  {variant.forkedFrom && (
                    <Link to={`/generator?variantId=${variant.forkedFrom.id}`} style={{ textDecoration: 'none', cursor: 'pointer' }}>
                        <Group gap={4} style={{ cursor: 'pointer' }}>
                            <IconGitFork size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
                            <Text size="xs" c="dimmed" style={{ cursor: 'pointer' }} className="hover-underline">
                                Forked from {variant.forkedFrom.user.name}/{variant.forkedFrom.name}
                            </Text>
                        </Group>
                    </Link>
                  )}
              </Stack>

              <Group justify="space-between" mt="md">
                  <Group gap={5}>
                    <ActionIcon 
                        variant="subtle" 
                        color={variant.likes?.includes(user?.id) ? "red" : "gray"}
                        onClick={() => handleLike(variant.id)} 
                        disabled={!user || likingInProgress.includes(variant.id)}
                    >
                        <IconHeart size={16} style={{ fill: variant.likes?.includes(user?.id) ? "currentColor" : "none" }} />
                    </ActionIcon>
                    <Text 
                        size="sm" 
                        c="dimmed" 
                        style={{ cursor: variant.likes?.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => handleShowLikers(variant)}
                    >
                        {variant.likes?.length || 0} likes
                    </Text>
                  </Group>
                  <Button variant="outline" size="xs" onClick={() => handleLoadVariant(variant)}>
                      Load in Generator
                  </Button>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
      <LikersModal isOpen={isLikersModalOpen} setIsOpen={setIsLikersModalOpen} likers={likers} variantName={selectedVariantName} />
    </Stack>
  );
}
